import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Atom,
  Lightning,
  Sparkle,
} from "@phosphor-icons/react";
import { FadeIn, Stagger, StaggerItem } from "../components/Motion";
import Marquee from "../components/Marquee";
import StackedCards from "../components/StackedCards";
import { SERVICES, STATS, VALUES } from "../data/site";


const officeImg =
  "https://images.unsplash.com/photo-1772001936267-b6058748eff4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBnbGFzcyUyMGNvcnBvcmF0ZSUyMG9mZmljZSUyMGludGVyaW9yfGVufDB8fHx8MTc3NjgzODYyN3ww&ixlib=rb-4.1.0&q=85";
const teamImg =
  "https://images.unsplash.com/photo-1758518731457-5ef826b75b3b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHRlYW0lMjBjb2xsYWJvcmF0aW9ufGVufDB8fHx8MTc3NjgzODYyN3ww&ixlib=rb-4.1.0&q=85";

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const heroOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.2]);

  return (
    <div data-testid="home-page">
      {/* ---------- HERO — editorial, Oryzo-style ---------- */}
      <section
        ref={heroRef}
        className="relative overflow-hidden pt-32 pb-12"
      >
        <motion.div
          style={{ y: heroY, opacity: heroOpacity }}
          className="absolute inset-0 pointer-events-none"
        >
          <div className="absolute inset-0 grid-bg" />
          <div className="absolute -top-20 -left-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-40"
               style={{ background: "radial-gradient(circle, #14B8A6 0%, transparent 65%)" }} />
          <div className="absolute top-40 right-0 w-[620px] h-[620px] rounded-full blur-3xl opacity-35"
               style={{ background: "radial-gradient(circle, #F97316 0%, transparent 65%)" }} />
          <div className="absolute bottom-0 left-1/3 w-[480px] h-[480px] rounded-full blur-3xl opacity-30"
               style={{ background: "radial-gradient(circle, #8B5CF6 0%, transparent 65%)" }} />
        </motion.div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 w-full text-center">
          {/* issue stripe */}
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="flex items-center justify-center gap-6 mb-8"
          >
            <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
            <span className="issue-stripe">Crafting with ❤ Studio Edition</span>
            <div className="h-[1px] w-16 bg-[#0b2e5e]/20" />
          </motion.div>

          {/* headline */}
          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="font-display text-[56px] sm:text-[88px] lg:text-[128px] leading-[0.9] tracking-[-0.03em] font-semibold text-[#0b2e5e]"
          >
            A studio <span className="font-serif-italic text-[#1d4ed8]">unreasonably</span>
            <br />
            obsessed with <span className="font-serif-italic text-[#e11d48]">the work.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2 }}
            className="mt-8 max-w-2xl mx-auto text-lg md:text-xl text-[#0b2e5e]/75 leading-relaxed"
          >
            We design, execute and scale category-defining campaigns for ambitious brands — with the bold obsessiveness of a boutique studio, the speed of a startup, and none of the freelance risk.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.35 }}
            className="mt-10 flex flex-wrap justify-center items-center gap-4"
          >
            <Link to="/contact" className="btn-primary" data-testid="hero-cta-primary">
              Start a project <ArrowUpRight weight="bold" size={16} />
            </Link>
            <Link to="/services" className="btn-ghost" data-testid="hero-cta-secondary">
              Our services <ArrowUpRight weight="bold" size={16} />
            </Link>
          </motion.div>
        </div>

        {/* featured orb with floating stickers */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 mt-16 md:mt-24">
          <div className="relative">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 40 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
              className="relative mx-auto w-[320px] sm:w-[420px] md:w-[540px]"
            >
              <div className="hero-disc" />
              {/* orbiting label */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <svg viewBox="0 0 200 200" className="absolute inset-0 w-full h-full" style={{ animation: "disc-spin 22s linear infinite reverse" }}>
                  <defs>
                    <path id="hero-ring" d="M 100, 100 m -86, 0 a 86,86 0 1,1 172,0 a 86,86 0 1,1 -172,0" />
                  </defs>
                  <text className="fill-[#0b2e5e]" style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 6, letterSpacing: 4 }}>
                    <textPath href="#hero-ring" startOffset="0">
                      MADLYDIGITAL · BRANDING · WEB-DESIGN · PHOTOGRAPHY · MOTION GRAPHICS · 
                    </textPath>
                  </text>
                </svg>
              </div>
            </motion.div>

            {/* floating stickers */}
            <motion.div initial={{ opacity: 0, y: 20, rotate: -6 }} animate={{ opacity: 1, y: 0, rotate: -6 }} transition={{ duration: 0.8, delay: 0.7 }} className="hidden md:block absolute top-6 left-2 lg:left-10 rotate-[-6deg]">
              <div className="sticker"><span className="dot" style={{ background: "#14B8A6" }} /> Digital Marketing</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: 5 }} animate={{ opacity: 1, y: 0, rotate: 5 }} transition={{ duration: 0.8, delay: 0.85 }} className="hidden md:block absolute top-20 right-2 lg:right-10 rotate-[5deg]">
              <div className="sticker"><span className="dot" style={{ background: "#F97316" }} /> Web Designs & Development</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: -3 }} transition={{ duration: 0.8, delay: 1 }} className="hidden md:block absolute bottom-12 left-4 lg:left-16 rotate-[-3deg]">
              <div className="sticker"><span className="dot" style={{ background: "#8B5CF6" }} /> Creative Design</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: 8 }} animate={{ opacity: 1, y: 0, rotate: 8 }} transition={{ duration: 0.8, delay: 1.15 }} className="hidden md:block absolute bottom-6 right-6 lg:right-20 rotate-[8deg]">
              <div className="sticker"><span className="dot" style={{ background: "#E11D48" }} /> Brand Strategy</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: -4 }} animate={{ opacity: 1, y: 0, rotate: -4 }} transition={{ duration: 0.8, delay: 1.3 }} className="hidden md:block absolute top-1/2 -translate-y-1/2 -left-4 lg:left-2 rotate-[-4deg]">
              <div className="sticker"><span className="dot" style={{ background: "#EAB308" }} /> Motion Graphics</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: 3 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ duration: 0.8, delay: 1.45 }} className="hidden md:block absolute top-1/2 -translate-y-1/2 -right-4 lg:right-0 rotate-[3deg]">
              <div className="sticker"><span className="dot" style={{ background: "#10B981" }} /> Photography</div>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20, rotate: 3 }} animate={{ opacity: 1, y: 0, rotate: 3 }} transition={{ duration: 0.8, delay: 1.45 }} className="hidden md:block absolute top-1/2 -translate-y-1/2 -right-4 lg:right-0 rotate-[3deg]">
              <div className="sticker"><span className="dot" style={{ background: "#e5742e" }} /> Events & Exhibitions</div>
            </motion.div>
          </div>
        </div>

        <div className="relative z-10 mt-16 flex justify-center text-[#0b2e5e]/50 text-xs">
          <div className="flex flex-col items-center gap-2">
            <span className="font-mono-mini">Scroll</span>
            <div className="h-10 w-[1px] bg-gradient-to-b from-[#0b2e5e]/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* ---------- STACKED CARDS — scroll-based featured work ---------- */}
      <section className="relative border-t border-[#0b2e5e]/8" data-testid="work-showcase">
        {/* Section header — sits above the scroll zone */}
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 pb-8">
          <FadeIn>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <div>
                <span className="issue-stripe">§ 001 — Featured Work</span>
                <h2 className="font-display text-3xl md:text-5xl mt-3 tracking-tight">
                  Scroll through{" "}
                  <span className="font-serif-italic">the archive.</span>
                </h2>
              </div>
              <Link to="/works" className="btn-ghost" data-testid="showcase-all-works">
                See all work <ArrowUpRight weight="bold" size={16} />
              </Link>
            </div>
          </FadeIn>
        </div>

        {/* The stacking cards — each card is sticky + scroll-scaled */}
        <StackedCards />
      </section>


      <Marquee />

      {/* ---------- STATS ---------- */}
      <section className="relative py-24 md:py-32" data-testid="stats-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Stagger className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-10">
            {STATS.map((s) => (
              <StaggerItem key={s.label}>
                <div className="glass tracing-border rounded-3xl p-6 md:p-8">
                  <div className="font-display text-5xl md:text-6xl grad-text">
                    {s.value}
                  </div>
                  <div className="mt-3 text-[#0b2e5e]/65 text-sm md:text-base">
                    {s.label}
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- ABOUT PREVIEW (BENTO) ---------- */}
      <section className="relative py-24 md:py-32" data-testid="about-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
              <div>
                <span className="tag mb-4">About the studio</span>
                <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4 max-w-2xl">
                  A small studio,
                  <br />
                  <span className="text-[#0b2e5e]/65">relentlessly obsessed</span> with the work.
                </h2>
              </div>
              <Link to="/about" className="btn-ghost" data-testid="about-learn-more">
                Learn more <ArrowUpRight weight="bold" size={16} />
              </Link>
            </div>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
            <FadeIn className="md:col-span-4">
              <div className="glass rounded-3xl overflow-hidden h-full min-h-[360px] relative group">
                <img
                  src={officeImg}
                  alt="Office"
                  className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-70 group-hover:scale-105 transition-all duration-[1200ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#FAF6EF] via-[#FAF6EF]/10 to-[#FAF6EF]/10" />
                <div className="relative z-10 h-full flex flex-col justify-end p-8">
                  <div className="font-mono-mini text-[#00F0FF]">01 / Studio</div>
                  <h3 className="font-display text-3xl md:text-4xl mt-2 max-w-md">
                    Madly Digital works with a select number of clients at a time — so every brand gets our full attention, not a fraction of it.
                  </h3>
                </div>
              </div>
            </FadeIn>

            <FadeIn className="md:col-span-2">
              <div className="glass rounded-3xl p-8 h-full flex flex-col justify-between min-h-[360px]">
                <div>
                  <div className="font-mono-mini text-[#FF3366]">02 / Approach</div>
                  <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight">
                  Every detail is a decision. Every decision is an argument for the client's audience. We don't do average.
                  </h3>
                </div>
                <div className="flex items-center gap-2 text-[#00F0FF] font-mono-mini mt-6">
                  <Sparkle weight="fill" /> Craft first
                </div>
              </div>
            </FadeIn>

            <FadeIn className="md:col-span-2">
              <div className="glass rounded-3xl p-8 h-full flex flex-col justify-between min-h-[280px]">
                <div>
                  <div className="font-mono-mini text-[#0b2e5e]/55">03 / Team</div>
                  <h3 className="font-display text-2xl md:text-3xl mt-3 leading-tight">
                    A tight-knit team of specialists across marketing, design, and development — all working as one squad.
                  </h3>
                </div>
                <p className="text-[#0b2e5e]/65 text-sm mt-6">
                  No juniors on projects. No outsourcing. Just the people who will actually do the work.
                </p>
              </div>
            </FadeIn>

            <FadeIn className="md:col-span-4">
              <div className="glass rounded-3xl overflow-hidden h-full min-h-[280px] relative group">
                <img
                  src={teamImg}
                  alt="Team"
                  className="absolute inset-0 h-full w-full object-cover opacity-45 group-hover:opacity-65 group-hover:scale-105 transition-all duration-[1200ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#FAF6EF] via-[#FAF6EF]/20 to-[#FAF6EF]/10" />
                <div className="relative z-10 h-full flex flex-col justify-between p-8">
                  <div className="font-mono-mini text-[#0b2e5e]/75">04 / People</div>
                  <div className="max-w-md">
                    <h3 className="font-display text-2xl md:text-3xl leading-tight">
                      No account managers playing telephone. You work directly with the people doing the work.
                    </h3>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ---------- SERVICES PREVIEW ---------- */}
      <section className="relative py-24 md:py-32" data-testid="services-preview">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
              <div>
                <span className="tag mb-4">What we do</span>
                <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4 max-w-2xl">
                  Six disciplines.
                  <br />
                  <span className="text-[#0b2e5e]/65">One compound team.</span>
                </h2>
              </div>
              <Link to="/services" className="btn-ghost" data-testid="services-see-all">
                See all services <ArrowUpRight weight="bold" size={16} />
              </Link>
            </div>
          </FadeIn>

          <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES.slice(0, 6).map((s, i) => (
              <StaggerItem key={s.id}>
                <Link to="/services" className="block group">
                  <div className="glass tracing-border rounded-3xl p-7 h-full hover:-translate-y-1 transition-transform duration-500">
                    <div
                      className="h-10 w-10 rounded-full flex items-center justify-center mb-6"
                      style={{ background: `${s.accent}18`, border: `1px solid ${s.accent}55` }}
                    >
                      <span className="font-display text-sm" style={{ color: s.accent }}>
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <h3 className="font-display text-2xl md:text-3xl leading-tight">
                      {s.title}
                    </h3>
                    <p className="text-[#0b2e5e]/65 mt-3 text-sm">{s.tagline}</p>
                    <div className="mt-8 flex items-center gap-2 text-[#0b2e5e]/55 text-sm group-hover:text-[#0b2e5e] transition-colors">
                      Explore <ArrowUpRight weight="bold" size={14} />
                    </div>
                  </div>
                </Link>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ---------- VALUES ---------- */}
      <section className="relative py-24 md:py-32" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <FadeIn>
            <span className="tag mb-4">Principles</span>
            <h2 className="font-display text-4xl md:text-6xl leading-[1.05] tracking-tight mt-4 max-w-3xl">
              Four principles we refuse to compromise.
            </h2>
          </FadeIn>

          <Stagger className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            {VALUES.map((v, i) => (
              <StaggerItem key={v.title}>
                <div className="border-t border-[#0b2e5e]/12 pt-8">
                  <div className="font-mono-mini text-[#0b2e5e]/50">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <h3 className="font-display text-3xl md:text-4xl mt-3">
                    {v.title}
                  </h3>
                  <p className="text-[#0b2e5e]/65 mt-4 text-base md:text-lg leading-relaxed max-w-lg">
                    {v.body}
                  </p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>
    </div>
  );
}
